import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, CreditCard, KeyRound, Tablet, Loader2, RefreshCw } from "lucide-react";

const empty = {
  nombre: "", ciudad: "", direccion: "", telefono: "", email: "",
  pais: "ES", comision_pct: 5, activa: true,
  razon_social: "", cif: "", direccion_fiscal: "", codigo_postal: "", provincia: "",
  serie_factura: "A", regimen_iva: "exento", iva_pct: 21, email_facturacion: "",
};

export default function Sedes() {
  const [sedes, setSedes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [estados, setEstados] = useState({});
  const [clavesOpen, setClavesOpen] = useState(null);
  const [claves, setClaves] = useState({ stripe_secret_key: "", stripe_webhook_secret: "" });
  const [pinOpen, setPinOpen] = useState(null);
  const [pin, setPin] = useState("");
  const [guardando, setGuardando] = useState(false);

  const load = async () => {
    const { data } = await api.get("/sedes");
    setSedes(data);
    data.forEach((s) =>
      api.get(`/sedes/${s.id}/stripe/estado`).then(({ data }) => setEstados((e) => ({ ...e, [s.id]: data }))).catch(() => {})
    );
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      const payload = { ...form, comision_pct: Number(form.comision_pct) || 0,
        iva_pct: Number(form.iva_pct) || 0 };
      if (editId) await api.put(`/sedes/${editId}`, payload);
      else await api.post("/sedes", payload);
      toast.success(editId ? "Sede actualizada" : "Sede creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/sedes/${id}`);
    toast.success("Sede eliminada");
    load();
  };

  const guardarClaves = async () => {
    setGuardando(true);
    try {
      const { data } = await api.post(`/sedes/${clavesOpen}/stripe/claves`, claves);
      toast.success(`Cuenta de Stripe vinculada (${data.modo}): ${data.stripe_account_id}`);
      setClavesOpen(null);
      setClaves({ stripe_secret_key: "", stripe_webhook_secret: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setGuardando(false);
    }
  };

  const cambiarModo = async (sedeId, modo) => {
    setGuardando(true);
    try {
      const { data } = await api.post(`/sedes/${sedeId}/stripe/modo`, { modo });
      toast.success(`Modo ${data.modo}: ${data.planes_aplicados} planes activos, ${data.planes_pendientes} por sincronizar`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setGuardando(false);
    }
  };

  const sincronizarPlanes = async (sedeId) => {
    setGuardando(true);
    try {
      const { data } = await api.post(`/sedes/${sedeId}/stripe/sincronizar-planes`);
      toast.success(`Planes en modo ${data.modo}: ${data.creados} creados, ${data.reutilizados} ya existían`,
        { duration: 8000 });
      if (data.errores?.length) toast.error(`${data.errores.length} planes con error: ${data.errores[0].error}`, { duration: 12000 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setGuardando(false);
    }
  };

  const guardarPin = async () => {
    try {
      await api.post(`/sedes/${pinOpen}/recepcion/pin`, { pin });
      toast.success("PIN de recepción actualizado");
      setPinOpen(null);
      setPin("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="sedes-page">
      <PageHeader
        title="Sedes"
        subtitle="Centros de Bspark, comisión de la plataforma y cuenta de cobro"
        action={
          <Button className="rounded-full font-semibold transition-transform duration-200 hover:-translate-y-[1px]"
            onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-sede-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva sede
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {sedes.map((s) => {
          const st = estados[s.id];
          return (
            <Card key={s.id} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`sede-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-accent grid place-items-center">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg leading-tight">{s.nombre}</h3>
                    <p className="text-xs text-muted-foreground">{s.ciudad}</p>
                  </div>
                </div>
                <Badge variant={s.activa ? "default" : "secondary"}>{s.activa ? "Activa" : "Inactiva"}</Badge>
              </div>
              <div className="mt-5 space-y-1 text-sm text-muted-foreground">
                {s.direccion && <p>{s.direccion}</p>}
                {s.telefono && <p>{s.telefono}</p>}
                {s.email && <p>{s.email}</p>}
                <p>Facturación: <b className="text-foreground">{s.cif || "sin CIF"}</b>
                  {" · serie "}{s.serie_factura || "A"}
                  {s.regimen_iva === "general" ? ` · IVA ${s.iva_pct}%` : " · exenta de IVA"}</p>
              </div>

              <div className="mt-5 rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Cuenta de Stripe de la sede</p>
                </div>
                {st?.conectada ? (
                  <>
                    <Badge variant={st.cobros_activos ? "default" : "secondary"} data-testid={`sede-stripe-badge-${s.id}`}>
                      {st.cobros_activos ? `Cobros activos (${st.modo || "test"})` : "Clave guardada, cobros no activos"}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-2 break-all">{st.stripe_account_id}</p>
                    {!st.webhook_configurado && (
                      <p className="text-xs text-amber-600 mt-1">Falta el secreto del webhook de esta cuenta.</p>
                    )}
                    {st.planes_pendientes > 0 && (
                      <p className="text-xs text-amber-600 mt-1" data-testid={`planes-pendientes-${s.id}`}>
                        {st.planes_pendientes} planes sin precios en esta cuenta ({st.modo}). Pulsa «Sincronizar planes».
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button variant="secondary" size="sm" className="rounded-lg" disabled={guardando}
                        onClick={() => sincronizarPlanes(s.id)} data-testid={`sincronizar-planes-${s.id}`}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Sincronizar planes
                      </Button>
                      {(st.modos_guardados || []).filter((m) => m !== st.modo).map((m) => (
                        <Button key={m} variant="outline" size="sm" className="rounded-lg" disabled={guardando}
                          onClick={() => cambiarModo(s.id, m)} data-testid={`modo-stripe-${m}-${s.id}`}>
                          Pasar a modo {m}
                        </Button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid={`sede-stripe-badge-${s.id}`}>
                    Sin claves propias — los cobros van a la cuenta principal de Bspark.
                  </p>
                )}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="rounded-lg"
                    onClick={() => setClavesOpen(s.id)} data-testid={`claves-stripe-${s.id}`}>
                    <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Claves de Stripe
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-lg"
                    onClick={() => setPinOpen(s.id)} data-testid={`pin-recepcion-${s.id}`}>
                    <Tablet className="h-3.5 w-3.5 mr-1.5" /> PIN recepción
                  </Button>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button variant="outline" size="sm" className="rounded-lg" data-testid={`edit-sede-${s.id}`}
                  onClick={() => { setForm({ ...empty, ...s }); setEditId(s.id); setOpen(true); }}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
                </Button>
                <Button variant="outline" size="sm" className="rounded-lg text-destructive" data-testid={`delete-sede-${s.id}`}
                  onClick={() => remove(s.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          );
        })}
        {sedes.length === 0 && <p className="text-muted-foreground text-sm">Todavía no hay sedes. Crea la primera.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="sede-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Editar sede" : "Nueva sede"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="sede-nombre-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Ciudad</Label>
                <Input value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} data-testid="sede-ciudad-input" />
              </div>
              <div className="grid gap-2">
                <Label>Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="sede-telefono-input" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Dirección</Label>
              <Textarea value={form.direccion} onChange={(e) => set("direccion", e.target.value)} data-testid="sede-direccion-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="sede-email-input" />
              </div>
              <div className="grid gap-2">
                <Label>Comisión Bspark (%)</Label>
                <Input type="number" step="0.5" value={form.comision_pct}
                  onChange={(e) => set("comision_pct", e.target.value)} data-testid="sede-comision-input" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label>Sede activa</Label>
              <Switch checked={form.activa} onCheckedChange={(v) => set("activa", v)} data-testid="sede-activa-switch" />
            </div>

            <div className="rounded-lg border border-border p-4 space-y-4">
              <p className="text-sm font-semibold">Datos fiscales (para las facturas)</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Razón social</Label>
                  <Input value={form.razon_social} onChange={(e) => set("razon_social", e.target.value)}
                    data-testid="sede-razon-social-input" />
                </div>
                <div className="grid gap-2">
                  <Label>CIF / NIF</Label>
                  <Input value={form.cif} onChange={(e) => set("cif", e.target.value)} data-testid="sede-cif-input" />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Domicilio fiscal</Label>
                <Input value={form.direccion_fiscal} onChange={(e) => set("direccion_fiscal", e.target.value)}
                  data-testid="sede-direccion-fiscal-input" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label>Código postal</Label>
                  <Input value={form.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)}
                    data-testid="sede-cp-input" />
                </div>
                <div className="grid gap-2">
                  <Label>Provincia</Label>
                  <Input value={form.provincia} onChange={(e) => set("provincia", e.target.value)}
                    data-testid="sede-provincia-input" />
                </div>
                <div className="grid gap-2">
                  <Label>Serie de factura</Label>
                  <Input value={form.serie_factura} onChange={(e) => set("serie_factura", e.target.value.toUpperCase())}
                    data-testid="sede-serie-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="grid gap-2 sm:col-span-2">
                  <Label>Régimen de IVA</Label>
                  <Select value={form.regimen_iva} onValueChange={(v) => set("regimen_iva", v)}>
                    <SelectTrigger data-testid="sede-regimen-iva-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exento">Exento (enseñanza, art. 20.Uno.9º LIVA)</SelectItem>
                      <SelectItem value="general">Con IVA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>IVA (%)</Label>
                  <Input type="number" value={form.iva_pct} onChange={(e) => set("iva_pct", e.target.value)}
                    disabled={form.regimen_iva === "exento"} data-testid="sede-iva-input" />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Email de facturación</Label>
                <Input value={form.email_facturacion} onChange={(e) => set("email_facturacion", e.target.value)}
                  data-testid="sede-email-facturacion-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={save} data-testid="save-sede-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!clavesOpen} onOpenChange={(v) => !v && setClavesOpen(null)}>
        <DialogContent className="rounded-2xl" data-testid="sede-claves-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Claves de Stripe de la sede</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Clave secreta (sk_...)</Label>
              <PasswordInput value={claves.stripe_secret_key} placeholder="sk_test_..."
                onChange={(e) => setClaves((c) => ({ ...c, stripe_secret_key: e.target.value }))}
                data-testid="sede-sk-input" />
            </div>
            <div className="grid gap-2">
              <Label>Secreto del webhook (whsec_...)</Label>
              <PasswordInput value={claves.stripe_webhook_secret} placeholder="whsec_..."
                onChange={(e) => setClaves((c) => ({ ...c, stripe_webhook_secret: e.target.value }))}
                data-testid="sede-whsec-input" />
              <p className="text-xs text-muted-foreground">
                En Stripe (con el interruptor <b>Modo test</b> apagado para live) ve a Desarrolladores → Webhooks →
                Añadir endpoint, apunta a <b>{window.location.origin}/api/stripe/webhook</b>, marca los eventos
                <b> checkout.session.completed</b>, <b>invoice.payment_succeeded</b> e <b>invoice.payment_failed</b>,
                y pega aquí su secreto (whsec_...). Se guardan por separado las claves test y las live: podrás
                conmutar entre ambos modos sin volver a introducirlas.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardarClaves} disabled={guardando}
              data-testid="save-sede-claves-btn">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar y verificar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinOpen} onOpenChange={(v) => !v && setPinOpen(null)}>
        <DialogContent className="rounded-2xl" data-testid="sede-pin-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">PIN de recepción</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            <Label>PIN de 4 a 6 dígitos</Label>
            <Input inputMode="numeric" maxLength={6} value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="sede-pin-input" />
            <p className="text-xs text-muted-foreground">
              Con este PIN se abre la pantalla de recepción (/recepcion) en la tablet de la entrada,
              donde los alumnos marcan su llegada.
            </p>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardarPin} disabled={pin.length < 4}
              data-testid="save-sede-pin-btn">Guardar PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
